import {
  useAllValidAnimals,
  useTotalCost,
} from "components/Signup/store/selectors/animals";
import { Button } from "ui-kit/Button";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { StepProps } from "components/Signup/steps/types";
import styles from "./QuotePreviewBottomBar.module.css";
import { BillingIntervalSelector } from "../BillingIntervalSelector";
import { switchAnimal } from "components/Signup/store/slices/animals";
import { SummaryStatement } from "../SummaryStatement";
import { useSequentialAnimals } from "components/Signup/flow/SignupFlow/useSequentialAnimals";
import { formatCurrency } from "utils/utils";
import { BillingIntervalText } from "../BillingIntervalText";
import { Typography } from "ui-kit/Typography";
import { useElementHeight } from "hooks/useElementHeight";
import { useState } from "react";
import { useWebTranslation } from "translations";
import { Icon } from "ui-kit";
import Colors from "ui-kit/colors";
import { useIsLoading } from "components/Signup/store/selectors/metadata";

export const QuotePreviewBottomBar = ({
  next,
  onPress,
  title,
}: {
  onPress?: VoidFunction;
  next: StepProps["next"];
  title?: string;
}) => {
  const { t } = useWebTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useSignupDispatch();
  const totalCost = useTotalCost();
  const { ref, height } = useElementHeight();
  const animals = useAllValidAnimals();
  const isLoading = useIsLoading();

  const { nextAnimalId } = useSequentialAnimals();

  return (
    <aside role="complementary" className={styles.container}>
      <div className={styles.sheetContainer} aria-hidden={!isOpen}>
        <div
          className={styles.sheet}
          style={{
            maxHeight: isOpen ? height : 0,
          }}
        >
          <div className={styles.sheetContent} ref={ref}>
            <BillingIntervalSelector />

            <div style={{ borderBottom: "1px solid lightgrey" }}>
              <SummaryStatement hideTotalRow />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.summaryContainer}>
        <button
          className={styles.summarySection}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup
          aria-controls="summary"
        >
          <Typography variant="body3">
            {t("signup.n_pet_total", { count: animals.length })}
          </Typography>
          <div className={styles.total}>
            <Typography variant="h3">
              {formatCurrency(totalCost.new)}
            </Typography>
            <BillingIntervalText color="grey500" />
          </div>
          <div className={styles.openCloseText}>
            <Typography variant="body3" color="blue300">
              {isOpen
                ? t("signup.quote_preview.hide_details")
                : t("signup.quote_preview.show_details")}
            </Typography>
            <Icon
              name={isOpen ? "chevron-down" : "chevron-up"}
              color={Colors.blue300}
              size="S"
              ariaHidden
            />
          </div>
        </button>
        <div className={styles.summarySection} id="summary">
          <Button
            title={
              title
                ? title
                : nextAnimalId
                ? t("signup.button.next_quote")
                : t("global.next")
            }
            onPress={() => {
              if (onPress) {
                onPress();
                return;
              }
              if (nextAnimalId) {
                scrollTo({ top: 0, behavior: "smooth" });
                dispatch(switchAnimal(nextAnimalId));
              } else {
                next();
              }
            }}
            loading={isLoading}
          />
        </div>
      </div>
    </aside>
  );
};
